// Constants
import { ClientFlavor, ClientType, MsaTenantId } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { HttpStatus, HttpStatusText } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { PlannerAsyncStoragePrefix, PlannerAsyncStorageSchemaVersionV1, PlannerAsyncStorageSchemaVersionV2, } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { SettingsObjectName } from "@ms/uno-constants/lib/local/configuration/ISettings";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Flux
import { AsyncDispatcher } from "@ms/uno-fluxcore/lib/local/dispatcher";
import { Store } from "@ms/uno-stores/lib/local/stores/Store";
// Host Adaptor
import { FailedReason } from "@ms/uno-hostadaptors/lib/local/IHostAdaptor";
// Models
import { AssetLoadingError } from "@ms/uno-errors/lib/local/errors/AssetLoadingError";
import { PersistedObjectStoreSchemaV1, PersistedObjectStoreSchemaV2, } from "@ms/uno-models/lib/local/client/persistence/PersistedAsyncStorageSchema";
// Services
import { AuthenticatedAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/AuthenticatedAjaxClient";
import { BasicAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/BasicAjaxClient";
import { GatedGetAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/GatedGetAjaxClient";
import { RequestPrioritizer } from "@ms/uno-services/lib/local/priorityRequestQueue/RequestPrioritizer";
import { generateAjaxClientError } from "@ms/uno-services/lib/local/utilities/ServiceUtilities";
import { Log } from "@ms/uno-telemetry/lib/local/events/Action.event";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { setIsOnlineStatusForLogs } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { setMomentCulture } from "@ms/uno-shell/lib/local/utilities/CultureUtilities";
import { isMetaOSHub } from "@ms/uno-utilities/lib/local/AppUtilities";
import { generateGuid } from "@ms/uno-utilities/lib/local/Guid";
import { HtmlSettingsReader } from "@ms/uno-utilities/lib/local/HtmlSettingsReader";
import { IndexedDBStorage } from "@ms/uno-utilities/lib/local/storage/IndexedDBStorage";
import { DataPreFetcher } from "../base/preFetcher/DataPreFetcher";
import { Worker } from "@ms/uno-workers/lib/local/workers/Worker";
/**
 * Sets up the AppContext
 * @throws {Error} If there is an error initializing the AppContext
 */
export function setupAppContext() {
    const reader = new HtmlSettingsReader(SettingsObjectName);
    /**
     * We need to generate a new client session id here because the server session id provided by MTRS will be unchanged in following cases-
     * 1. First load after ServiceWorker installation (applies to all new SW installations)
     * 2. Failure to fetch initial HTML from frontend in case the server is down (edge case)
     */
    const clientSessionId = generateGuid();
    AppContext.initializeFromSettings(AppContext.BuildContextSettingsFromSettingsGetter(reader.getSetting.bind(reader)), clientSessionId);
}
/**
 * Gets the context from hostAdaptor and also sets moment culture data
 */
export async function getContextAndSetMomentCulture(hostAdaptor) {
    let context;
    try {
        context = await hostAdaptor.getContext();
        setMomentCulture(context.locale);
    }
    catch (e) {
        const err = new AssetLoadingError(e.name, e.message);
        hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message: e.message });
        // TODO - 7545465, 8108486 need to implement errors and error logging
        // Task 8108486: Uno LazyLoading and asset loading error management
        throw err;
    }
    return context;
}
/**
 * Gets the TelemetryContext from the Context
 */
export function getTelemetryContext(context) {
    return {
        locale: context.locale,
        userId: context.userId,
        tenantId: context.tenantId,
        ringId: context.ringId, // Teams ring according to Teams client SDK
        isMSA: context.tenantId === MsaTenantId,
        isGuestUser: context.isGuestUser,
        platform: context.platform,
    };
}
/**
 * Gets the StoreProviders from the StoreFactory
 */
export function getStoreProviders(storeFactory) {
    return {
        load: storeFactory.load.bind(storeFactory),
        loadAll: storeFactory.loadAll.bind(storeFactory),
        aggregates: storeFactory.getAggregatesStoreProvider(),
        activity: storeFactory.getActivityStoreProvider(),
        auth: storeFactory.getAuthStoreProvider(),
        appContext: storeFactory.getAppContextStoreProvider(),
        basicBucket: storeFactory.getBasicBucketStoreProvider(),
        basicGoal: storeFactory.getBasicGoalStoreProvider(),
        basicPlan: storeFactory.getBasicPlanStoreProvider(),
        basicPlanConversionStatus: storeFactory.getBasicPlanConversionStatusStoreProvider(),
        basicPermissions: storeFactory.getBasicPermissionsStoreProvider(),
        basicTask: storeFactory.getBasicTaskStoreProvider(),
        campaign: storeFactory.getCampaignStoreProvider(),
        capabilities: storeFactory.getCapabilitiesStoreProvider(),
        chat: storeFactory.getChatStoreProvider(),
        legacyContainer: storeFactory.getLegacyContainerStoreProvider(),
        conversations: storeFactory.getConversationsStoreProvider(),
        createTaskFromMessage: storeFactory.getCreateTaskFromMessageStoreProvider(),
        deltaSync: storeFactory.getDeltaSyncStoreProvider(),
        driveItem: storeFactory.getDriveItemStoreProvider(),
        externalAgent: storeFactory.getExternalAgentStoreProvider(),
        groups: storeFactory.getGroupsStoreProvider(),
        intelligence: storeFactory.getIntelligenceStoreProvider(),
        modal: storeFactory.getModalStoreProvider(),
        mru: storeFactory.getMruStoreProvider(),
        navigation: storeFactory.getNavigationStoreProvider(),
        newsBar: storeFactory.getNewsBarStoreProvider(),
        notification: storeFactory.getNotificationStoreProvider(),
        legacyNotification: storeFactory.getLegacyNotificationStoreProvider(),
        operationTracking: storeFactory.getOperationTrackingStoreProvider(),
        org: storeFactory.getOrgStoreProvider(),
        performanceCollection: storeFactory.getPerformanceCollectionStoreProvider(),
        planConversion: storeFactory.getPlanConversionStoreProvider(),
        portfolio: storeFactory.getPortfolioStoreProvider(),
        premiumPlan: storeFactory.getPremiumPlanStoreProvider(),
        premiumPlanPermissions: storeFactory.getPremiumPlanPermissionsStoreProvider(),
        projectHomeResource: storeFactory.getProjectHomeResourceStoreProvider(),
        publishing: storeFactory.getPublishingStoreProvider(),
        route: storeFactory.getRouteStoreProvider(),
        statusReport: storeFactory.getStatusReportStoreProvider(),
        suiteNav: storeFactory.getSuiteNavStoreProvider(),
        teams: storeFactory.getTeamsStoreProvider(),
        templates: storeFactory.getTemplatesStoreProvider(),
        tenant: storeFactory.getTenantStoreProvider(),
        upsell: storeFactory.getUpsellStoreProvider(),
        unoShell: storeFactory.getUnoShellStoreProvider(),
        user: storeFactory.getUserStoreProvider(),
        userMembership: storeFactory.getUserMembershipStoreProvider(),
        legacyView: storeFactory.getLegacyViewStoreProvider(),
        recommendedPlans: storeFactory.getRecommendedPlansStoreProvider(),
    };
}
/**
 * Gets the AsyncDispatcher
 * @param configProvider The config provider
 * @param registry flux registry
 * @param storeFactory store providers
 * @param loggers used for logging
 */
export function getAsyncDispatcher(configProvider, registry, storeFactory, loggers) {
    return new AsyncDispatcher(registry, (storeType) => () => storeFactory.loadForDispatch(storeType), (storeType) => () => storeFactory.loadForSubscription(storeType), (event) => Log(event, loggers.logHandler), (tag, traceLevel, message) => loggers.traceLogger.logTrace(tag, traceLevel, message), configProvider().flights.EnableAsyncDispatchV2);
}
/**
 * Gets the RequestPrioritizer
 */
export function getRequestPrioritizer(storeProviders) {
    return new RequestPrioritizer((request) => {
        // Cancel request if it is not for the current view
        const currentViewId = storeProviders.load(Store.Route).getCurrentViewId();
        return currentViewId != null && request.viewIds != null && request.viewIds.indexOf(currentViewId) === -1;
    });
}
/**
 * Method to get persistent async storage instance
 * @param configProvider config provider
 * @param userId user id
 * @param loggers used for logging
 * @returns persisted async storage instance or null if data persistence is disabled or IndexedDB is not supported
 */
export function getPersistentAsyncStorage(configProvider, userId, loggers) {
    let persistentAsyncStorage = null;
    if (configProvider().flights.EnableDataPersistence && userId) {
        if (IndexedDBStorage.isIndexedDBSupported()) {
            try {
                persistentAsyncStorage = new IndexedDBStorage(userId, PlannerAsyncStoragePrefix + "_" + configProvider().settings.persistentAsyncStorageVersion, getPersistentAsyncStorageVersion(configProvider), getPersistentAsyncStorageSchema(configProvider), loggers.traceLogger);
            }
            catch (error) {
                loggers.traceLogger.logTrace(0x1e25684e /* tag_4jw7o */, TraceLevel.Error, error);
            }
        }
        else {
            loggers.traceLogger.logTrace(0x1e28338e /* tag_4kdoo */, TraceLevel.Info, "IndexedDB is not supported");
        }
    }
    return persistentAsyncStorage;
}
/**
 * Method to get the persistent async storage version
 * @param configProvider config provider
 */
export function getPersistentAsyncStorageVersion(configProvider) {
    return configProvider().flights.EnableTasksDataPersistence && configProvider().settings.persistenceSettings.tasks
        ? PlannerAsyncStorageSchemaVersionV2
        : PlannerAsyncStorageSchemaVersionV1;
}
/**
 * Method to get the persistent async storage schema
 * @param configProvider config provider
 */
export function getPersistentAsyncStorageSchema(configProvider) {
    return configProvider().flights.EnableTasksDataPersistence && configProvider().settings.persistenceSettings.tasks
        ? PersistedObjectStoreSchemaV2
        : PersistedObjectStoreSchemaV1;
}
/**
 * Gets the AuthService
 */
export function getAuthServiceProvider(factory, dispatcher, hostAdaptor, loggers, configProvider) {
    const { auth } = configProvider().settings.serviceConfigurations;
    const { globalAjaxTimeout } = configProvider().settings.retrySettings;
    const apiClient = new AuthenticatedAjaxClient(new BasicAjaxClient(globalAjaxTimeout, loggers), auth.authResourceURI, loggers, hostAdaptor.getAccessToken);
    const gatedApiClient = new GatedGetAjaxClient(apiClient);
    return factory.getAuthServiceProvider(gatedApiClient, hostAdaptor, configProvider);
}
/**
 * Gets the ServiceProviders
 */
export function getServiceProviders(serviceOrchestrator, authService) {
    return {
        adaptiveCard: serviceOrchestrator.getAdaptiveCardServiceProvider(),
        augloop: serviceOrchestrator.getAugloopServiceProvider(),
        auth: () => authService,
        centro: serviceOrchestrator.getCentroServiceProvider(),
        chatSvcAgg: serviceOrchestrator.getChatSvcAggServiceProvider(),
        conversations: serviceOrchestrator.getConversationsServiceProvider(),
        driveItem: serviceOrchestrator.getDriveItemServiceProvider(),
        flow: serviceOrchestrator.getFlowServiceProvider(),
        gateway: serviceOrchestrator.getGatewayServiceProvider(),
        iris: serviceOrchestrator.getIrisServiceProvider(),
        groups: serviceOrchestrator.getGroupsServiceProvider(),
        legacyChatSvcAgg: serviceOrchestrator.getLegacyChatSvcAggServiceProvider(),
        loop: serviceOrchestrator.getLoopServiceProvider(),
        loopMts: serviceOrchestrator.getLoopMtsServiceProvider(),
        mars: serviceOrchestrator.getMarsServiceProvider(),
        mru: serviceOrchestrator.getMruServiceProvider(),
        ocps: serviceOrchestrator.getOcpsServiceProvider(),
        oneshell: serviceOrchestrator.getOneShellServiceProvider(),
        planner: serviceOrchestrator.getPlannerServiceProvider(),
        plannerGraph: serviceOrchestrator.getPlannerGraphServiceProvider(),
        plp: serviceOrchestrator.getLegacyPlpServiceProvider(),
        projectLandingPage: serviceOrchestrator.getProjectLandingPageServiceProvider(),
        pss: serviceOrchestrator.getProjectSchedulingServiceProvider(),
        publishing: serviceOrchestrator.getPublishingServiceProvider(),
        tcms: serviceOrchestrator.getTcmsServiceProvider(),
        teams: serviceOrchestrator.getTeamsServiceProvider(),
        tenant: serviceOrchestrator.getTenantServiceProvider(),
        plannerTenant: serviceOrchestrator.getPlannerTenantServiceProvider(),
        retail: serviceOrchestrator.getRetailServiceProvider(),
        roaming: serviceOrchestrator.getRoamingServiceProvider(),
        siteURL: serviceOrchestrator.getSiteURLServiceProvider(),
        substrateSearch: serviceOrchestrator.getSubstrateSearchServiceProvider(),
        todo: serviceOrchestrator.getTodoServiceProvider(),
        user: serviceOrchestrator.getUserServiceProvider(),
        search: serviceOrchestrator.getSearchServiceProvider(),
        msTeams: serviceOrchestrator.getMsTeamsServiceProvider(),
        modernGroup: serviceOrchestrator.getModernGroupsServiceProvider(),
        odsp: serviceOrchestrator.getOdspServiceProvider(),
        trouter: () => {
            throw new Error("NotSupported");
        },
    };
}
/**
 * Gets the LogicModuleProviders
 */
export function getLogicModuleProviders(lmFactory, dispatcher, serviceProviders, storeProviders, configProvider, hostAdaptor, authService, authStore, fetchAccessToken) {
    return {
        adaptiveCard: lmFactory.getAdaptiveCardModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        appContext: lmFactory.getAppContextModuleProvider(dispatcher, serviceProviders, storeProviders, hostAdaptor, configProvider),
        auth: lmFactory.getAuthModuleProvider(dispatcher, authService, authStore, configProvider),
        basicBucket: lmFactory.getBasicBucketModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        basicGoal: lmFactory.getBasicGoalModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        basicPlan: lmFactory.getBasicPlanModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        basicTask: lmFactory.getBasicTaskModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        campaign: lmFactory.getCampaignModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        capabilities: lmFactory.getCapabilitiesModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        centro: lmFactory.getCentroModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        chat: lmFactory.getChatModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        chatSvcAgg: lmFactory.getChatSvcAggModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        conversations: lmFactory.getConversationsModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        deltaSync: lmFactory.getDeltaSyncModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        excelExport: lmFactory.getExcelExportModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        externalAgent: lmFactory.getExternalAgentsModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        driveItem: lmFactory.getDriveItemModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        groups: lmFactory.getGroupsModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        intelligence: lmFactory.getIntelligenceModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        legacyLinks: lmFactory.getLegacyLinksModuleProvider(dispatcher, serviceProviders, storeProviders, hostAdaptor, configProvider),
        links: lmFactory.getLinksModuleProvider(dispatcher, serviceProviders, storeProviders, hostAdaptor, configProvider),
        loop: lmFactory.getLoopModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        mru: lmFactory.getMruModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        notification: lmFactory.getNotificationModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        org: lmFactory.getOrgModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        performanceCollection: lmFactory.getPerformanceCollectionModuleProvider(dispatcher),
        planai: lmFactory.getPlanAiModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        planConversion: lmFactory.getPlanConversionModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        portfolio: lmFactory.getPortfolioModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        premiumPlan: lmFactory.getPremiumPlanModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        premiumTask: lmFactory.getPremiumTaskModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        publishing: lmFactory.getPublishingModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider, fetchAccessToken),
        roaming: lmFactory.getRoamingModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        roster: lmFactory.getRosterModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        sensitivityUserRights: lmFactory.getSensitivityUserRightsModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        suiteNav: lmFactory.getSuiteNavModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        teams: lmFactory.getTeamsModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        templates: lmFactory.getTemplateModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        tenant: lmFactory.getTenantModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        todo: lmFactory.getTodoModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider, fetchAccessToken),
        ttTabs: lmFactory.getTeamsTasksTabsModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        upsell: lmFactory.getUpsellModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
        user: lmFactory.getUserModuleProvider(dispatcher, serviceProviders, storeProviders, configProvider),
    };
}
/**
 * Gets the ActionCreatorProviders
 */
export function getActionCreatorProviders(acFactory) {
    return {
        load: acFactory.load.bind(acFactory),
        activity: acFactory.getActivityAcProvider(),
        appContext: acFactory.getAppContextAcProvider(),
        basicBucket: acFactory.getBasicBucketAcProvider(),
        basicPlan: acFactory.getBasicPlanAcProvider(),
        basicTask: acFactory.getBasicTaskAcProvider(),
        campaign: acFactory.getCampaignAcProvider(),
        capabilities: acFactory.getCapabilitiesAcProvider(),
        chat: acFactory.getChatAcProvider(),
        container: acFactory.getContainerAcProvider(),
        createTaskFromTeamsMessage: acFactory.getCreateTaskFromTeamsMessageAcProvider(),
        dataPreFetch: acFactory.getDataPreFetchAcProvider(),
        deltaSync: acFactory.getDeltaSyncAcProvider(),
        driveItem: acFactory.getDriveItemAcProvider(),
        export: acFactory.getExportAcProvider(),
        externalAgent: acFactory.getExternalAgentAcProvider(),
        goal: acFactory.getGoalAcProvider(),
        groups: acFactory.getGroupsAcProvider(),
        intelligence: acFactory.getIntelligenceAcProvider(),
        keyboardHandlers: acFactory.getKeyboardHandlersAcProvider(),
        links: acFactory.getLinksAcProvider(),
        modal: acFactory.getModalAcProvider(),
        mru: acFactory.getMruAcProvider(),
        newsBar: acFactory.getNewsBarAcProvider(),
        notification: acFactory.getNotificationAcProvider(),
        operationTracking: acFactory.getOperationTrackingAcProvider(),
        org: acFactory.getOrgAcProvider(),
        performanceCollection: acFactory.getPerformanceCollectionAcProvider(),
        planAi: acFactory.getPlanAiAcProvider(),
        portfolio: acFactory.getPortfolioAcProvider(),
        publishing: acFactory.getPublishingAcProvider(),
        planConversion: acFactory.getPlanConversionAcProvider(),
        premiumPlan: acFactory.getPremiumPlanAcProvider(),
        routing: acFactory.getRoutingAcProvider(),
        suiteNavView: acFactory.getSuiteNavViewAcProvider(),
        teams: acFactory.getTeamsAcProvider(),
        teamsTab: acFactory.getTeamsTabAcProvider(),
        templates: acFactory.getTemplatesAcProvider(),
        tenant: acFactory.getTenantAcProvider(),
        unoShell: acFactory.getUnoShellAcProvider(),
        upsell: acFactory.getUpsellAcProvider(),
        user: acFactory.getUserAcProvider(),
        ocv: acFactory.getOCVAcProvider(),
        recommendedPlans: acFactory.getRecommendedPlansAcProvider(),
        projectHomeResource: acFactory.getResourceAcProvider(),
        // View AC providers
        basicPlanView: acFactory.getBasicPlanViewAcProvider(),
        createPlanView: acFactory.getCreatePlanViewAcProvider(),
        copyPlanView: acFactory.getCopyPlanViewAcProvider(),
        createPortfolioView: acFactory.getCreatePortfolioViewAcProvider(),
        createTaskView: acFactory.getCreateTaskViewAcProvider(),
        leftNavView: acFactory.getLeftNavViewAcProvider(),
        myDayView: acFactory.getMyDayViewAcProvider(),
        myPlansView: acFactory.getMyPlansViewAcProvider(),
        myPortfoliosView: acFactory.getMyPortfoliosViewAcProvider(),
        myTasksView: acFactory.getMyTasksViewAcProvider(),
        unoShellView: acFactory.getUnoShellViewAcProvider(),
        convertPlanView: acFactory.getConvertPlanViewAcProvider(),
        createTeamsTabView: acFactory.getCreateTeamsTabViewAcProvider(),
        sharePlanView: acFactory.getSharePlanViewAcProvider(),
        publishingLeftNavView: acFactory.getPublishingLeftNavViewAcProvider(),
        campaignCalloutView: acFactory.getCampaignCalloutViewAcProvider(),
        viewTransforms: acFactory.getViewTransformsAcProvider(),
    };
}
/**
 * Gets the agent providers from the agent factory
 */
export function getAgentProviders(agentFactory) {
    const agentProviders = {
        projectManager: agentFactory.getProjectManagerAgentProvider(),
    };
    return agentProviders;
}
/**
 * Load chunks and initialize deferrable workers
 */
export function loadAndInitializeDeferrableWorkers(workerLoader, loggers, persistentAsyncStorage, configProvider) {
    try {
        const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
        workerLoader.load(Worker.PerformanceObserver).init();
        if (clientFlavor !== ClientFlavor.TaskFromMessage) {
            workerLoader.load(Worker.DeltaSyncFeedProcessor).init();
            workerLoader.load(Worker.Mru).init();
            workerLoader.load(Worker.Modal).init();
            workerLoader.load(Worker.Navigation).init();
            workerLoader.load(Worker.PremiumPlanCreationStatus).init();
            workerLoader.load(Worker.ProjectTrialOperationTracker).init();
            workerLoader.load(Worker.AugloopTileSync).init();
            workerLoader.load(Worker.OperationTracking).init();
            workerLoader.load(Worker.BasicPlanContainerFetcher).init();
            workerLoader.load(Worker.BasicPlanOpenTracker).init();
            if (configProvider().flights.EnableCampaignTriggerFromWorker) {
                workerLoader.load(Worker.Campaign).init();
            }
        }
        if (configProvider().flights.EnableDataPersistence) {
            if (persistentAsyncStorage !== null) {
                workerLoader.load(Worker.CacheUpdate).init();
                if (configProvider().flights.EnableTasksDataPersistence) {
                    workerLoader.load(Worker.CacheEviction).init();
                }
            }
            else {
                loggers.traceLogger.logTrace(0x1e25b68f /* tag_4j10p */, TraceLevel.Info, "Persistent Async Storage is not available. Skipping CacheUpdate worker initialization.");
            }
        }
        if (configProvider().flights.EnableUnoLevelProjectProvisioning &&
            configProvider().settings.EnvironmentFeatureSettings.EnablePremiumPlanExperience) {
            workerLoader.load(Worker.CdsProvisioningStatus).init();
        }
        const shouldInitializeStatusTrackerWorker = configProvider().flights.EnableUpsellConversion &&
            (configProvider().sessionMetaData.appMetadata.clientType === ClientType.Teams
                ? clientFlavor !== ClientFlavor.TaskFromMessage
                : true);
        if (shouldInitializeStatusTrackerWorker) {
            if (configProvider().flights.EnableBasicPlanParallelConversion) {
                workerLoader.load(Worker.PlanConversionStatusTracker).init();
            }
            else {
                workerLoader.load(Worker.BasicPlanConversionTracker).init();
            }
        }
        const shouldInitializeServiceWorkerBootstrap = configProvider().flights.EnableServiceWorker &&
            (configProvider().sessionMetaData.appMetadata.clientType === ClientType.Teams ||
                configProvider().sessionMetaData.appMetadata.clientType === ClientType.Web);
        if (shouldInitializeServiceWorkerBootstrap) {
            workerLoader.load(Worker.ServiceWorkerBootstrap).init();
        }
        if (clientFlavor === ClientFlavor.Tab) {
            workerLoader.load(Worker.TeamsTab).init();
        }
    }
    catch (e) {
        loggers.traceLogger.logTrace(0x1e25b68e /* tag_4j10o */, TraceLevel.Warning, `Failed to load and initialize deferrable workers. [ErrorType=${e.name}][Error=${e.message}]`);
    }
}
/**
 * Load chunks and initialize critical workers
 */
export function loadAndInitializeWorkers(workerLoader, requestPrioritizer, loggers, persistentAsyncStorage, configProvider, hostAdaptor, cachedLoadId) {
    try {
        // 1. Initialize the static workers
        workerLoader.load(Worker.DataFetch).init(); // critical because we want data fetch to be as fast as possible
        const bootObserverWorker = workerLoader.load(Worker.BootObserver); // critical because we want to make sure we are able to detect exactly when boot is complete
        bootObserverWorker.init();
        if (configProvider().flights.EnableInstantiatePremiumPlanFromWorker &&
            configProvider().settings.EnvironmentFeatureSettings.EnablePremiumPlanExperience) {
            workerLoader.load(Worker.ChildAppInstantiationWorker).init(); // critical to ensure that child app is booted as quickly as possible
        }
        if (configProvider().flights.EnableDataPersistence) {
            if (persistentAsyncStorage !== null) {
                workerLoader.load(Worker.StoreHydration).init(); // critical because we want to load the cached data into the stores
            }
            else {
                loggers.traceLogger.logTrace(0x1e25b68d /* tag_4j10n */, TraceLevel.Info, "Persistent Async Storage is not available. Skipping StoreHydration worker initialization.");
            }
        }
        // 2. Initialize the workers that are not static but critical be loaded before critical boot
        const networkConnectivityWorker = workerLoader.load(Worker.NetworkConnectivity);
        networkConnectivityWorker.init(); // critical because we want to show some skeleton UI in the absence of a network connection
        networkConnectivityWorker.addNetworkConnectivityChangeCallback(requestPrioritizer.onNetworkConnectivityChanged.bind(requestPrioritizer));
        networkConnectivityWorker.addNetworkConnectivityChangeCallback(setIsOnlineStatusForLogs);
        if (configProvider().sessionMetaData.appMetadata.clientFlavor !== ClientFlavor.TaskFromMessage) {
            workerLoader.load(Worker.DeltaSync).init(); // critical because delta sync must happen before any data calls are made to ensure correctness
        }
        if (!configProvider().flights.EnableWorkerDeferredInit) {
            loadAndInitializeDeferrableWorkers(workerLoader, loggers, persistentAsyncStorage, configProvider);
        }
        // 3. Actions to be taken after critical boot is complete
        bootObserverWorker.onBootCompletion(() => {
            // Notify render success on boot completion
            if (configProvider().sessionMetaData.appMetadata.clientType === ClientType.Web ||
                (isMetaOSHub(configProvider().sessionMetaData.appMetadata.clientType) &&
                    configProvider().flights.EnableTeamsNotifySuccessV2)) {
                hostAdaptor.notifyRenderSuccess();
            }
            // Mark the cached boot completion if boot was resumed from a cached load
            if (cachedLoadId) {
                loggers.performanceMarker.mark(MarkerTag.CachedLoadComplete, {
                    cachedLoadId, // Unique identifier for the particular cached load instance
                    bootCompletedOnCachedLoad: "true", // Indicates if the boot was completed on cached load
                });
            }
            // Load and initialize the workers that are not critical
            if (configProvider().flights.EnableWorkerDeferredInit) {
                loadAndInitializeDeferrableWorkers(workerLoader, loggers, persistentAsyncStorage, configProvider);
            }
        });
    }
    catch (e) {
        loggers.traceLogger.logTrace(0x1e25b68c /* tag_4j10m */, TraceLevel.Warning, `Failed to load and initialize pre-boot workers. [ErrorType=${e.name}][Error=${e.message}]`);
    }
}
/**
 * Log initial plan view sub route type info for experimentation
 * @param componentName The component name
 * @param subRouteType The current sub route type
 * @param loggers The loggers
 */
export function logPlanViewSubRouteType(componentName, subRouteType, loggers) {
    // If initial route is for a plan view, track in engagement telemetry
    if (subRouteType === SubRouteType.BasicPlan || subRouteType === SubRouteType.PremiumPlan || subRouteType === SubRouteType.TodoList) {
        loggers.engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.OpenPlanFromPersistedRoute, {
            subRouteType: subRouteType,
        });
    }
}
/**
 * Initializes the data pre-fetcher for the application.
 * @param deepLinkRoute The deep link route descriptor, if any
 * @param context The application context
 * @param hostAdaptor The host adaptor
 * @param loggers The logger context
 * @param configProvider The configuration provider for feature flags and settings
 */
export function initDataPreFetcher(deepLinkRoute, context, hostAdaptor, loggers, configProvider) {
    const dataPreFetcher = new DataPreFetcher(deepLinkRoute, context, async (resource) => {
        try {
            loggers.performanceMarker.mark(MarkerTag.GetAccessTokenStart);
            const token = await hostAdaptor.getAccessToken(resource);
            loggers.performanceMarker.mark(MarkerTag.GetAccessTokenComplete);
            return token.accessToken;
        }
        catch (error) {
            if (configProvider().flights.EnableErrorMessageLogging) {
                // The adapter will eventually go into the BaseMsalHelper which logs the exact AuthError
                // We just need to wrap this error into a failed request result
                loggers.traceLogger.logTrace(0x1e15b78b /* tag_4f14l */, TraceLevel.Warning, `Could not get an auth token: [ErrorType=${error?.name}]`);
                const errorText = "Could not get an access token: AppBootstrap";
                const failureResult = generateAjaxClientError(new Response(errorText, { status: HttpStatus.Unauthorized, statusText: HttpStatusText.Unauthorized }), errorText);
                return Promise.reject(failureResult);
            }
            else {
                // If error message logging is disabled, we throw the error directly
                return Promise.reject(error);
            }
        }
    });
    dataPreFetcher.init();
}
